import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BridgeClubLayout from '../../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import './FarmChallengeHub.css';

interface RedemptionTier {
  id: number;
  points_required: number;
  reward_value: number;
  reward_description: string;
  is_available: boolean;
}

interface TiersResponse {
  tiers: RedemptionTier[];
}

const FarmChallengeRedeem: React.FC = () => {
  const [balance, setBalance] = useState<number>(0);
  const [tiers, setTiers] = useState<RedemptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState<number | null>(null);
  const [success, setSuccess] = useState<{ code: string; value: number } | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusData, tiersData] = await Promise.all([
        bridgeClubAPI.getFarmChallengeStatus(),
        bridgeClubAPI.getRedemptionTiers()
      ]);
      setBalance(statusData.current_balance || 0);
      setTiers((tiersData as TiersResponse).tiers || []);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load redemption data:', err);
      setError('Failed to load redemption options');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRedeem = async (tier: RedemptionTier) => {
    if (redeeming) return;
    if (balance < tier.points_required) return;

    setRedeeming(tier.id);
    setError(null);

    try {
      const response = await bridgeClubAPI.createFarmChallengeRedemption({
        tier_id: tier.id
      });

      if (response.success) {
        setSuccess({
          code: response.redemption_code,
          value: response.value
        });
        // Refresh balance
        loadData();
      } else {
        setError(response.message || 'Failed to redeem points');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to redeem points');
    } finally {
      setRedeeming(null);
    }
  };

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="farm-challenge-loading">
          <div className="spinner"></div>
          <p>Loading redemption options...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  if (error && tiers.length === 0) {
    return (
      <BridgeClubLayout>
        <div className="farm-challenge-error">
          <p>{error}</p>
          <button onClick={loadData}>Try Again</button>
        </div>
      </BridgeClubLayout>
    );
  }

  const dollarValue = balance / 100;

  return (
    <BridgeClubLayout>
      <div className="farm-challenge-hub">
        <div className="hub-header">
          <Link to="/bridge-club/farm-challenge" className="back-link">
            ← Back
          </Link>
          <h1>Redeem Points</h1>
        </div>

        {/* Current Balance */}
        <div className="points-card redeem-balance">
          <div className="points-main">
            <span className="points-balance">{balance.toLocaleString()}</span>
            <span className="points-label">points available</span>
          </div>
          <div className="points-value-row">
            <span className="dollar-value">${dollarValue.toFixed(2)} value</span>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="redeem-success">
            <span className="success-icon">🎉</span>
            <h3>Redemption Successful!</h3>
            <p className="success-value">${success.value.toFixed(2)} Feed Credit</p>
            <div className="success-code">
              <span className="code-label">Your Code:</span>
              <span className="code-value">{success.code}</span>
            </div>
            <p className="success-note">
              Show this code to the cashier at your next purchase.
            </p>
            <button onClick={() => setSuccess(null)} className="btn-primary">
              Done
            </button>
          </div>
        )}

        {/* Redemption Tiers */}
        {!success && (
          <>
            <h2 className="section-title">Redemption Options</h2>

            {tiers.length === 0 ? (
              <div className="activity-empty">
                <p>No redemption options available at this time.</p>
              </div>
            ) : (
              <div className="redeem-tiers">
                {tiers.map((tier) => {
                  const canRedeem = balance >= tier.points_required;
                  const isRedeeming = redeeming === tier.id;

                  return (
                    <div
                      key={tier.id}
                      className={`redeem-tier ${canRedeem ? 'available' : 'locked'}`}
                    >
                      <div className="tier-info">
                        <span className="tier-value">${tier.reward_value.toFixed(2)}</span>
                        <span className="tier-description">{tier.reward_description}</span>
                        <span className="tier-points">{tier.points_required.toLocaleString()} points</span>
                      </div>
                      <button
                        className={`tier-btn ${canRedeem ? 'btn-primary' : 'btn-disabled'}`}
                        onClick={() => handleRedeem(tier)}
                        disabled={!canRedeem || isRedeeming}
                      >
                        {isRedeeming ? 'Redeeming...' : canRedeem ? 'Redeem' : 'Need More'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {error && <div className="redeem-error">{error}</div>}

            <div className="redeem-info">
              <h3>How It Works</h3>
              <ul>
                <li>100 points = $1.00 in feed credits</li>
                <li>Credits can be used at any participating store</li>
                <li>Redemption codes expire in 90 days</li>
                <li>Show your code to the cashier at checkout</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default FarmChallengeRedeem;
